import {FC, ReactElement, cloneElement} from 'react'

import {RBACEyeIcon, TooltipBody, TooltipWrapper} from 'components'
import {usePermissions} from './PermissionsContext'

interface CanIAccessProps {
  children: ReactElement
  reqPermissions: string[]
  showEyeIcon?: boolean
  fallbackComponent?: JSX.Element | null
  injectProps?: Record<string, unknown>
}

export const EyeComponent = () => (
  <span data-tooltip-id="read-only-perm-tooltip">
    <TooltipWrapper
      id="read-only-perm-tooltip"
      className="ml-1 z-50" // z-50 to make sure this tooltip always comes above everything else.
      noArrow
      place="right"
      noPadding
    >
      <TooltipBody>You have read-only permissions</TooltipBody>
    </TooltipWrapper>
    <RBACEyeIcon />
  </span>
)

export const CanIAccess: FC<CanIAccessProps> = ({
  children,
  reqPermissions,
  showEyeIcon = false,
  fallbackComponent = null,
  injectProps = {
    isReadOnly: true,
  },
  ...props
}) => {
  const {permissions} = usePermissions()

  if (!permissions) return null

  // Check if user is admin
  if (permissions === '*') return <>{children}</>

  // Extract read and write permissions
  const readPermissions = reqPermissions.filter(perm => perm.includes(':read'))
  const writePermissions = reqPermissions.filter(perm =>
    perm.includes(':write'),
  )

  // Determine if the user has the required read and/or write permissions
  const hasReadPermission = readPermissions.some(perm =>
    permissions.includes(perm),
  )
  const hasWritePermission = writePermissions.some(perm =>
    permissions.includes(perm),
  )

  // Show and enable the component if write permission is present
  if (hasWritePermission) return <>{children}</>

  if (!hasReadPermission && !hasWritePermission) {
    return showEyeIcon ? <EyeComponent /> : fallbackComponent // Hide the component if permissions are not met
  }

  /**
   * prop injection to children from HOC
   * to control states of child component as per permissions
   */
  if (hasReadPermission && !hasWritePermission) {
    return showEyeIcon ? (
      <EyeComponent />
    ) : (
      cloneElement(children, {...props, ...injectProps})
    )
  }

  return fallbackComponent
}
